import type { ClientSettings } from "@cleo/contracts";
import { create } from "zustand";

import {
  getClientSettings,
  persistClientSettings,
  subscribeClientSettings,
  useClientSettings,
} from "../hooks/useSettings";
import { contextOpenLadder, egoWishLadder } from "../neucleos/agentRailFold";
import {
  PRIMARY_AGENT_RAIL_FIXED_WIDTH,
  PRIMARY_AGENT_RAIL_MAX_WIDTH,
  PRIMARY_AGENT_RAIL_MIN_WIDTH,
  PRIMARY_AGENT_RAIL_WIDE_WIDTH,
} from "./agentRailWidth";
import { CONTEXT_RAIL_MIN_WIDTH, CONTEXT_RAIL_RIB_WIDTH } from "./contextRailWidth";
import { DESK_DOCK_TIGHT_PX } from "./dockPreferences";

// Widen Cleo (dated 2026-10-07): one client setting, `cleoPanelWide`, kept per
// Mac and never part of any World's remembered room. The panel's own button,
// View > Widen/Restore Cleo and Cleo's set_setting (the settings door writes
// the client-settings file this window re-hydrates from) all change this one
// switch: the person's act and Cleo's are the same act.
export function readCleoWide(): boolean {
  return getClientSettings().cleoPanelWide;
}

export function writeCleoWide(next: boolean): void {
  // Turning Widen on is the newer act: a held Context open ends, as if the
  // rail had been folded, and Widen takes the room back. (The hold's own
  // watch ends it too when the setting changes; this covers a press while
  // Widen is stored on but paused, which changes nothing in the file.)
  if (next) useDeskWidthStore.getState().setContextOpen(false);
  persistClientSettings({ cleoPanelWide: next });
}

/** Toggles what the person sees: a paused Widen reads as off, so the press turns it on. */
export function toggleCleoWide(): void {
  writeCleoWide(!(readCleoWide() && !useDeskWidthStore.getState().contextOpen));
}

/**
 * The dock's own expand is the newer hand act: it ends whatever holds the
 * dock tight. While a Context open holds, the open is the cause: it ends,
 * and Widen (paused, never rewritten) is turned off only when the plan
 * without the hold would still keep the dock tight.
 */
export function releaseDockYield(): void {
  const desk = useDeskWidthStore.getState();
  if (desk.contextOpen) {
    const widenStillTight = desk.plan?.dockTightWithoutHold === true;
    desk.setContextOpen(false);
    if (!widenStillTight) return;
  }
  if (readCleoWide()) writeCleoWide(false);
}

const selectCleoWide = (settings: ClientSettings): boolean => settings.cleoPanelWide;

/** The stored switch itself, paused or not: the shell's plan reads it. */
export function useCleoWideSetting(): boolean {
  return useClientSettings(selectCleoWide);
}

/**
 * Whether Widen Cleo holds: the person's switch, paused (never rewritten)
 * while a Context open holds the room, so ending the open brings Widen back
 * exactly as it was.
 */
export function useCleoWide(): readonly [boolean, (next: boolean) => void] {
  const wide = useCleoWideSetting();
  const contextHeld = useDeskWidthStore((state) => state.contextOpen);
  return [wide && !contextHeld, writeCleoWide] as const;
}

// While a Context open holds, Widen turned on by ANY writer (the panel's
// button, the View menu, Cleo's set_setting through the settings file) ends
// the hold, so the setting takes effect visibly. Listening only while the
// hold lasts; the hold itself never writes `cleoPanelWide`.
let stopWidenWatch: (() => void) | null = null;

function watchWidenDuringHold(open: boolean): void {
  if (!open) {
    stopWidenWatch?.();
    stopWidenWatch = null;
    return;
  }
  if (stopWidenWatch !== null) return;
  let wide = readCleoWide();
  stopWidenWatch = subscribeClientSettings(() => {
    const next = readCleoWide();
    const turnedOn = next && !wide;
    wide = next;
    if (turnedOn) useDeskWidthStore.getState().setContextOpen(false);
  });
}

/**
 * What the session shell's plan decided, for the two pieces of furniture it
 * does not render itself: the Cleo Panel (the room) and the left dock. The
 * shell is the one publisher; with no seated room nothing is published and
 * both render from their own preferences, exactly as before.
 */
export interface DeskWidthPlan {
  /** The column the shell reserves for Cleo; the context law reads the same number. */
  readonly basePx: number;
  /** The widest Cleo asks for; the room grows her into free deck space up to it. */
  readonly wishPx: number;
  /** Widen Cleo needs the dock's room: render it tight, its preference untouched. */
  readonly dockTight: boolean;
  /**
   * While Widen Cleo holds, the widest the dock may be dragged without moving
   * Cleo or folding it on release; null when the dock answers only to itself.
   */
  readonly dockMaxPx: number | null;
  /**
   * Present (true) only when the folded Context rail could not open here even
   * with the dock tight and Cleo at her minimum: the rib says so instead.
   */
  readonly contextShort?: true;
  /**
   * Present (true) only while a Context open holds and the plan WITHOUT the
   * hold would keep the dock tight too (Widen's own need): expanding the dock
   * then ends the hold and turns Widen off; otherwise it ends the hold only.
   */
  readonly dockTightWithoutHold?: true;
}

interface DeskWidthState {
  /** The dock row's inner width (dock + center + context), measured by the frame. */
  readonly frameWidth: number | null;
  /** The dock's preferred rendered width, published by the dock. */
  readonly dockPreferredPx: number | null;
  /**
   * The seated room's NESTED rail count, published by the room itself while it
   * renders a Cleo Panel; null with no room. The shell reads it where it has
   * no World lineage of its own (Local and draft rooms).
   */
  readonly seatedRails: number | null;
  readonly plan: DeskWidthPlan | null;
  /**
   * The person pressed the folded CONTEXT rib (dated 2026-10-07): the rail is
   * held open, the dock and Cleo yielding through the plan. Held in memory
   * only, never stored; folding the rail ends it and every width re-derives.
   * A plan that reports `contextShort` (the window narrowed past what any
   * yield can hold) ends it too: Widen resumes and the rib returns.
   */
  readonly contextOpen: boolean;
  setFrameWidth: (width: number | null) => void;
  setDockPreferredPx: (width: number | null) => void;
  setSeatedRails: (rails: number | null) => void;
  publishPlan: (plan: DeskWidthPlan | null) => void;
  setContextOpen: (open: boolean) => void;
}

export const useDeskWidthStore = create<DeskWidthState>((set, get) => ({
  frameWidth: null,
  dockPreferredPx: null,
  seatedRails: null,
  plan: null,
  contextOpen: false,
  setFrameWidth: (frameWidth) => set({ frameWidth }),
  setDockPreferredPx: (dockPreferredPx) => set({ dockPreferredPx }),
  setSeatedRails: (seatedRails) => set({ seatedRails }),
  publishPlan: (plan) => {
    set((state) =>
      state.plan?.basePx === plan?.basePx &&
      state.plan?.wishPx === plan?.wishPx &&
      state.plan?.dockTight === plan?.dockTight &&
      state.plan?.dockMaxPx === plan?.dockMaxPx &&
      state.plan?.contextShort === plan?.contextShort &&
      state.plan?.dockTightWithoutHold === plan?.dockTightWithoutHold
        ? state
        : { plan },
    );
    // No silent leftover hold: a rail that can no longer stand ends the open.
    if (plan?.contextShort === true && get().contextOpen) get().setContextOpen(false);
  },
  setContextOpen: (contextOpen) => {
    watchWidenDuringHold(contextOpen);
    set({ contextOpen });
  },
}));

/**
 * The shell's plan, pure: Cleo's stored preference splits into the fixed
 * column (up to the old 480 ceiling) and the wish above it. Without Widen the
 * wish only ever takes free deck space (the room's half), so the shared
 * ladder runs with no wish and the dock is never asked. With Widen the wish
 * is twice the old ceiling (or the preference, if wider) and the ladder may
 * fold the dock for it. Before the frame and the dock have reported their
 * widths, the plan reserves the fixed column only.
 *
 * A held Context open (the rib pressed, dated 2026-10-07) reads the same row
 * from the context's side: the dock renders tight first, then Cleo's column
 * narrows toward her minimum, and Widen is paused while it holds (the
 * caller passes the stored switch; the plan pauses it here, never writing
 * it). The plan also says when even that could not open the rail, so the rib
 * can say so before it is pressed, and whether the plan without the hold
 * would keep the dock tight too.
 */
export function planDeskWidth(input: {
  readonly frameWidth: number | null;
  readonly preferredPx: number;
  readonly wide: boolean;
  readonly totalRails: number;
  readonly agentRailPx: number;
  readonly dockPreferredPx: number | null;
  readonly hasContextRail: boolean;
  /** The person pressed the folded CONTEXT rib and the open still holds. */
  readonly contextOpen?: boolean;
}): DeskWidthPlan {
  const preferred = Math.min(
    PRIMARY_AGENT_RAIL_MAX_WIDTH,
    Math.max(PRIMARY_AGENT_RAIL_MIN_WIDTH, input.preferredPx),
  );
  const basePx = Math.min(preferred, PRIMARY_AGENT_RAIL_FIXED_WIDTH);
  const contextStep =
    input.frameWidth === null || input.dockPreferredPx === null || !input.hasContextRail
      ? null
      : contextOpenLadder({
          width: input.frameWidth,
          totalRails: input.totalRails,
          agentRailPx: input.agentRailPx,
          ego: { basePx, minPx: PRIMARY_AGENT_RAIL_MIN_WIDTH },
          contextMinPx: CONTEXT_RAIL_MIN_WIDTH,
          dock: { fullPx: input.dockPreferredPx, tightPx: DESK_DOCK_TIGHT_PX },
        });
  const short = contextStep !== null && !contextStep.fits ? { contextShort: true as const } : {};
  const ordinary = (wide: boolean): DeskWidthPlan => {
    if (!wide) return { basePx, wishPx: preferred, dockTight: false, dockMaxPx: null, ...short };
    const wishPx = Math.max(preferred, PRIMARY_AGENT_RAIL_WIDE_WIDTH);
    if (input.frameWidth === null || input.dockPreferredPx === null) {
      return { basePx, wishPx, dockTight: false, dockMaxPx: null };
    }
    const step = egoWishLadder({
      width: input.frameWidth,
      totalRails: input.totalRails,
      agentRailPx: input.agentRailPx,
      ego: { basePx, wishPx },
      contextRibPx: input.hasContextRail ? CONTEXT_RAIL_RIB_WIDTH : null,
      dock: { fullPx: input.dockPreferredPx, tightPx: DESK_DOCK_TIGHT_PX },
    });
    return {
      basePx: step.egoPx,
      wishPx,
      dockTight: step.dockTight,
      dockMaxPx: Number.isFinite(step.dockMaxPx) ? step.dockMaxPx : null,
      ...short,
    };
  };
  if (input.contextOpen !== true) return ordinary(input.wide);
  if (contextStep === null || !contextStep.fits) return ordinary(false);
  return {
    basePx: contextStep.egoPx,
    wishPx: preferred,
    dockTight: contextStep.dockTight,
    dockMaxPx: Number.isFinite(contextStep.dockMaxPx) ? contextStep.dockMaxPx : null,
    ...(ordinary(input.wide).dockTight ? { dockTightWithoutHold: true as const } : {}),
  };
}
